import "./AppInstance.scss"

import { KeyboardEvent, ReactNode, useEffect, useRef, useState } from "react"

import { AppInstance } from "@/entities/app/app.types"
import OCBAppDAO from "@/entities/app/ocb/ocb.dao"
import Modal from "@/services/Modal/Modal"
import Expander from "@/shared/components/extrinsic/Expander/Expander"
import Tumbler from "@/shared/components/extrinsic/Tumbler/Tumbler"
import ButtonIcon from "@/shared/components/intrinsic/Button/ButtonIcon"
import FieldAdaptive from "@/shared/components/intrinsic/Field/FieldAdaptive"
import Icon from "@/shared/components/intrinsic/Icon/Icon"
import { classWithModifiers, inputValue, toggleState } from "@/utils/common"

import PopupAppInstanceDeletionConfirm from "../../modals/PopupAppInstanceDeletionConfirm"

const ocbAppDao = new OCBAppDAO

export interface AppInstanceViewProps extends AppInstance {
  name: string
  label?: string
  onNameChange?(value: string): void | Promise<void>

  active?: boolean
  onActiveChange?(active: boolean): void

  editing?: boolean
  onEdit?(): void

  onDelete?(): void

  children?: ReactNode

  scene: string | null
}

function AppInstanceView(props: AppInstanceViewProps) {
  const [expanded, setExpanded] = useState(false)

  function onDelete() {
    Modal.open(PopupAppInstanceDeletionConfirm, { onConfirm: props.onDelete })
  }

  return (
    <div className={classWithModifiers("app-instance", props.editing && "editing")}>
      <div className="app-instance__header">
        {props.children && <button type="button" onClick={toggleState(setExpanded)}><Icon name={expanded ? "cross" : "plus"} /></button>}
        <AppInstanceTitle value={props.label || props.name} hoverValue={props.name} onChange={props.onNameChange} />
        <div className="app-instance__actions">
          {props.active != null && (
            <div className="app-instance__toggle">
              <Tumbler checked={props.active} readonly={props.onActiveChange == null} onChange={props.onActiveChange} />
            </div>
          )}
          <div className="app-instance__tools">
            {props.onEdit && !props.scene && (
              <button className="app-instance__button" type="button" onClick={props.onEdit}>Edit</button>
            )}
            {props.onDelete && (
              <ButtonIcon size="small" color="transparent" name="trash-bin" onClick={onDelete} ariaLabel="Delete" />
            )}
          </div>
        </div>
      </div>
      {props.children && <Expander expanded={expanded}><div className="app-instance__container">{props.children}</div></Expander>}
    </div>
  )
}

interface AppInstanceTitleProps {
  value?: string
  hoverValue?: string
  onChange?(value: string): void | Promise<void>
}

/**
 * Has optimistic update.
 */
function AppInstanceTitle(props: AppInstanceTitleProps) {
  const [value, setValue] = useState(props.value)
  const [initialValue, setInitialValue] = useState(props.value)
  const [editable, setEditable] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)

  async function onSubmit(value: string) {
    //await props.onChange?.(value)
    if (props.hoverValue && value) {
      const result = await ocbAppDao.updateName(props.hoverValue, value)
      if (result.name === value) {
        setInitialValue(value)
        setEditable(false)
      }
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (inputRef.current == null) return

    if (event.code === "Enter") {
      event.preventDefault()
      onSubmit(event.currentTarget.value)
      setEditable(false)

      return
    }

    if (event.code === "Escape") {
      event.preventDefault()
      setValue(props.value)
      setEditable(false)

      return
    }
  }

  useEffect(() => {
    if (editable) {
      inputRef.current?.focus()
      inputRef.current?.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length)
    }
  }, [editable])

  return (
    <div className="app-instance-title" >
      {!editable && <ButtonIcon size="small" color="transparent" name="pen" onClick={() => { setEditable(true) }} ariaLabel="Edit POS Config" />}
      {editable && <ButtonIcon size="small" color="transparent" name="cross-circle" onClick={() => { setEditable(false); setValue(initialValue) }} ariaLabel="Edit POS Config" />}
      {editable && <ButtonIcon disabled={((!value && value?.trim().length === 0) || value === initialValue || value?.trim().length === 0)} size="small" color="transparent" name="check" onClick={() => onSubmit(value!)} ariaLabel="Edit POS Config" />}
      <FieldAdaptive className="app-instance-title__input" disabled={!editable} value={value} onChange={inputValue(setValue)} title={props.hoverValue} onKeyDown={onKeyDown} _ref={inputRef} />
      {/* <button className={classWithModifiers("app-instance-title__toggle", editable && "editable")} type="button" onClick={toggleState(setEditable)}>
        <Icon name="pen" />
      </button> */}
    </div>
  )
}

export default AppInstanceView
